/**
 * Meeting-level payroll outcomes.
 *
 * Authoritative store: public.payroll_meeting_exclusions
 *   (instructor_id, class_slug, meeting_date) UNIQUE
 *   columns: outcome, reason (admin note), covered_by, reason_code
 *
 * The payroll engine reads this table on every recalculation, so an outcome
 * recorded here permanently removes that single meeting from the assigned
 * instructor's payable meetings — without deleting the meeting, changing the
 * assignment, or touching the instructor's normal per-meeting rate.
 *
 * "Taught as Scheduled" is represented by the ABSENCE of a row.
 */
import { supabase } from "@/integrations/supabase/client";

export type MeetingOutcome =
  | "taught_as_scheduled"
  | "instructor_did_not_teach"
  | "class_cancelled"
  | "substitute_taught"
  | "other";

export const MEETING_OUTCOMES: Array<{ value: MeetingOutcome; label: string; help: string }> = [
  { value: "taught_as_scheduled", label: "Taught as Scheduled", help: "Instructor receives their normal meeting compensation." },
  { value: "instructor_did_not_teach", label: "Instructor Did Not Teach", help: "Assigned instructor receives $0 for this meeting. The meeting stays in history." },
  { value: "class_cancelled", label: "Class Cancelled", help: "Meeting did not happen. Assigned instructor receives $0 and the meeting stays in history, marked cancelled." },
  { value: "substitute_taught", label: "Substitute Taught", help: "Original instructor receives $0. Substitute pay is not automated — record it separately." },
  { value: "other", label: "Other", help: "Requires a note. Records $0 for this meeting for the assigned instructor." },
];

export function outcomeLabel(outcome?: string | null): string {
  return MEETING_OUTCOMES.find((o) => o.value === outcome)?.label
    ?? (outcome ? "Not payable" : "Taught as Scheduled");
}

export interface MeetingOutcomeRecord {
  id: string;
  instructor_id: string;
  class_slug: string;
  session_label: string | null;
  meeting_date: string;
  outcome: MeetingOutcome;
  reason: string | null;
  covered_by: string | null;
}

/** All recorded (non-taught) outcomes for one instructor. */
export async function listMeetingOutcomes(instructorId: string): Promise<MeetingOutcomeRecord[]> {
  const { data, error } = await supabase
    .from("payroll_meeting_exclusions")
    .select("id, instructor_id, class_slug, session_label, meeting_date, outcome, reason, covered_by")
    .eq("instructor_id", instructorId);
  if (error) throw error;
  return (data ?? []) as unknown as MeetingOutcomeRecord[];
}

export async function setMeetingOutcome(input: {
  instructorId: string;
  classSlug: string;
  sessionLabel: string | null;
  meetingDate: string;
  outcome: MeetingOutcome;
  note?: string | null;
}): Promise<void> {
  const note = (input.note ?? "").trim();
  if (input.outcome === "other" && !note) {
    throw new Error("A note is required when the outcome is “Other”.");
  }

  if (input.outcome === "taught_as_scheduled") {
    const { error } = await supabase
      .from("payroll_meeting_exclusions")
      .delete()
      .eq("instructor_id", input.instructorId)
      .eq("class_slug", input.classSlug)
      .eq("meeting_date", input.meetingDate);
    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from("payroll_meeting_exclusions")
    .upsert(
      {
        instructor_id: input.instructorId,
        class_slug: input.classSlug,
        session_label: input.sessionLabel,
        meeting_date: input.meetingDate,
        outcome: input.outcome,
        reason_code: input.outcome,
        reason: note || outcomeLabel(input.outcome),
      } as any,
      { onConflict: "instructor_id,class_slug,meeting_date" },
    );
  if (error) throw error;
}
